import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Region } from '@app/@core/enums';
import { AppEventService, AppEventType } from '@app/@core/services/app-event.service';
import { Address } from '@app/@theme/google-map/models/address';
import { geocodingAddress } from '@app/pages/customer/models/customer.model';
import { DropDownModel } from '@app/pages/customer/models/dropdown.model';
import { AddressModel } from '@app/shared/models/address.model';
import { Location } from '@core/models/location';
import { NbToastrService } from '@nebular/theme';
import { Observable, of, Subscription } from 'rxjs';
import { debounce, mergeMap, throttleTime } from 'rxjs/operators';

@Component({
    selector: 'de-address',
    templateUrl: './address.component.html',
    styleUrls: ['./address.component.scss'],
    standalone: false
})
export class AddressComponent implements OnInit, OnDestroy {
  @Input() addressForm: FormGroup;
  @Input() sourceAddressForm: FormGroup;
  @Input() title: string = '';
  @Input() showUseAddressAsCheckBox: boolean = false;
  @Input() useAddressAsLabel: string = '';
  @Input() set addressData(data: AddressModel) {
    if (data) {
      this._addressData = data;
      this.patchValue(data);
    }
  }
  @Output() validationFinished = new EventEmitter<boolean>(false);
  @Input() regionDropDown: Array<DropDownModel> = [];
  private _addressData: AddressModel;
  private sourceAddressValueChangeSubscription: Subscription;
  private appEventSub: Subscription;

  // regionDropDown: Array<DropDownModel> = [
  //   { value: Region.North, display: 'North' },
  //   { value: Region.South, display: 'South' },
  // ];

  constructor(
    private appEventService: AppEventService,
    private toastrService: NbToastrService) {
    this.appEventSub = this.appEventService.on(AppEventType.ProcessAddressValidation)
      .pipe(throttleTime(1000))
      .subscribe(_ => {
        this.addressGeoValidation();
      });
  }

  private addressGeoValidation() {
    this.getGeocodeAddress().subscribe((gMapLocation: Location) => {
      // update latlng from geocoding
      if (gMapLocation) {
        if (!gMapLocation.longitude || !gMapLocation.latitude) {
          this.addressForm.patchValue({
            latitude: 0,
            longitude: 0,
          });
          this.toastrService.danger('', `System cannot find ${this.title}`);
        } else {
          this.addressForm.patchValue({
            address1: gMapLocation.address,
            city: gMapLocation.city,
            state: gMapLocation.state,
            zipCode: gMapLocation.zipCode,
            country: gMapLocation.country,
            latitude: gMapLocation.latitude,
            longitude: gMapLocation.longitude,
          });
        }
      }

      this.appEventService.dispatch(AppEventType.AddressValidationFinished);
    });
  }

  // ====== Geocoding ======
  private getGeocodeAddress(): Observable<Location> {
    // Dont need to call geocoder api without changes of delivery addresses
    if (!this.isAddressChanged(this.addressForm)) {
      return of(null);
    }

    const formattedAddress = `${this.addressForm.controls['address1'].value},
        ${this.addressForm.controls['city'].value},
        ${this.addressForm.controls['state'].value},
        ${this.addressForm.controls['zipCode'].value},
        ${this.addressForm.controls['country'].value}
    `;

    return geocodingAddress(formattedAddress);
  }

  isAddressChanged(deliveryAddressForm) {
    if (!this._addressData) {
      return true;
    }
    return this._addressData.address1 !== deliveryAddressForm.controls['address1'].value ||
      this._addressData.city !== deliveryAddressForm.controls['city'].value ||
      this._addressData.state !== deliveryAddressForm.controls['state'].value ||
      this._addressData.zipCode !== deliveryAddressForm.controls['zipCode'].value ||
      this._addressData.country !== deliveryAddressForm.controls['country'].value;
  }
  ngOnDestroy() {
    if (this.sourceAddressValueChangeSubscription) {
      this.sourceAddressValueChangeSubscription.unsubscribe();
    }
    if (this.appEventSub) {
      this.appEventSub.unsubscribe();
    }
  }

  ngOnInit() {
    this.initAddressFormControls();
  }
  patchValue(data: AddressModel) {
    this.addressForm.patchValue({
      id: data.id,
      address1: data.address1,
      address2: data.address2,
      state: data.state,
      regionId: data.regionId,
      city: data.city,
      zipCode: data.zipCode,
      country: data.country,
      latitude: data.latitude,
      longitude: data.longitude,
      sameAddressAs: data.isSameOfficeAddress,
    });
    if (data.isSameOfficeAddress) {
      this.sourceAddressValueChangeSubscription = this.sourceAddressForm.valueChanges.subscribe(_ => this.cloneAddress(this.sourceAddressForm, this.addressForm));
    }
  }
  initAddressFormControls() {
    this.addressForm.addControl('id', new FormControl());
    this.addressForm.addControl('address1', new FormControl('', Validators.compose([Validators.required, Validators.maxLength(255)])));
    this.addressForm.addControl('address2', new FormControl('', Validators.compose([Validators.maxLength(255)])));
    this.addressForm.addControl('state', new FormControl('', Validators.compose([Validators.required, Validators.maxLength(255)])));
    this.addressForm.addControl('regionId', new FormControl());
    this.addressForm.addControl('city', new FormControl('', Validators.compose([Validators.required, Validators.maxLength(255)])));
    this.addressForm.addControl('zipCode', new FormControl('', Validators.compose([Validators.required, Validators.maxLength(255)])));
    this.addressForm.addControl('country', new FormControl());
    this.addressForm.addControl('latitude', new FormControl());
    this.addressForm.addControl('longitude', new FormControl());
    this.addressForm.addControl('sameAddressAs', new FormControl(false));
  }

  // Google autocomplete address on select
  onLocationSelected(event: Address) {
    const gMapLocation = Location.getGmapAddress(event);
    this.addressForm.patchValue({
      address1: gMapLocation.address,
      address2: '',
      city: gMapLocation.city,
      state: gMapLocation.state,
      zipCode: gMapLocation.zipCode,
      country: gMapLocation.country,
      latitude: gMapLocation.latitude,
      longitude: gMapLocation.longitude,
    });
    this.cloneAddress(this.sourceAddressForm, this.addressForm);
  }

  onSameAsOtherAddressChecked(checked: boolean) {
    this.addressForm.controls.sameAddressAs.setValue(checked);
    if (checked) {
      this.cloneAddress(this.sourceAddressForm, this.addressForm);

      this.sourceAddressValueChangeSubscription = this.sourceAddressForm.valueChanges
        .subscribe(_ => this.cloneAddress(this.sourceAddressForm, this.addressForm));
    } else {
      this.clearBillingAddress(this.addressForm);
      this.sourceAddressValueChangeSubscription.unsubscribe();
    }
  }

  clearBillingAddress(billingAddress: FormGroup) {
    billingAddress.patchValue({
      address1: '',
      address2: '',
      state: '',
      regionId: '',
      city: '',
      zipCode: '',
      country: '',
      latitude: '',
      longitude: '',
      sameAddressAs: false
    });
  }

  // Copy address data from A to B
  cloneAddress(a: FormGroup, b: FormGroup) {
    if (this.addressForm.value.sameAddressAs) {
      b.patchValue(a.value)
      b.controls.sameAddressAs.setValue(true);
    }
  }
}
